<script setup lang="ts">
import { inject, ref, toRaw, watch } from "vue";
import {
  FormElementType,
  MailingAddressAnswerValue,
  MailingAddressFormElement,
} from "~/api";
import { useIsInPdfMode } from "~/stores/pdfModeStore";
import { isFullMailingAddress } from "~/utils/typeGuards";
import {
  FormResponseState,
  formResponseInjectKey,
} from "../../composables/useFormResponse";
import MailingAddressInput from "../MailingAddressInput.vue";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props =
  defineProps<DefaultFormComponentProps<MailingAddressFormElement>>();

const { useAnswer } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const answer = useAnswer<FormElementType.MAILING_ADDRESS>(props.modelValue);
const localAnswer = ref<Partial<MailingAddressAnswerValue> | undefined>(
  structuredClone(toRaw(answer.value)),
);

// Re-sync the local copy when the answer changes externally — a failed
// save/delete rolls the value back, or a server reload updates it. Deep compare
// so the user's own edits (which already set localAnswer) don't trigger a
// redundant reset; the watch only fires when answer.value actually changes.
watch(
  () => answer.value,
  (newVal) => {
    if (JSON.stringify(newVal) !== JSON.stringify(toRaw(localAnswer.value))) {
      localAnswer.value = newVal ? structuredClone(toRaw(newVal)) : undefined;
    }
  },
);

const isBlank = (value?: string) => !value || value.trim() === "";

const handleUpdateAnswer = (
  newVal: Partial<MailingAddressAnswerValue> | undefined,
) => {
  localAnswer.value = newVal;

  // When the meaningful fields are all cleared, delete the saved answer.
  // The input never emits undefined (it accumulates empty-string fields), and
  // an empty object would be *saved* rather than deleted, so explicitly set
  // undefined to take the delete path. Province alone isn't a real address.
  const isEmptyAddress =
    !newVal ||
    (isBlank(newVal.streetAddress) &&
      isBlank(newVal.city) &&
      isBlank(newVal.postalCode));

  if (isEmptyAddress) {
    if (answer.value !== undefined) {
      answer.value = undefined;
    }
    return;
  }

  // Validate before submitting
  if (
    isFullMailingAddress(newVal) &&
    newVal.postalCode.length === 7 &&
    newVal.streetAddress.length > 0 &&
    newVal.city.length > 0
  ) {
    answer.value = newVal;
  }
};

const inPdf = useIsInPdfMode();
</script>

<template>
  <div class="MailingAddressComponent">
    <slot name="label" :element="modelValue" />

    <MailingAddressInput
      v-if="!inPdf || !localAnswer?.streetAddress"
      :model-value="localAnswer"
      :disabled="isReadOnly"
      @update:model-value="handleUpdateAnswer"
    />
    <div v-else class="pdf-address pdf-span">
      <div>{{ localAnswer?.streetAddress }}</div>
      <div>
        {{ localAnswer?.city }}, {{ localAnswer?.province }} &nbsp;
        {{ localAnswer?.postalCode }}
      </div>
    </div>
  </div>
</template>

<style lang="less">
.MailingAddressComponent {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: flex-start;
  justify-content: space-between;
}

body.forPdf {
  .MailingAddressComponent {
    .province-field {
      .el-select {
        background-color: white;
        .el-select__wrapper {
          padding: 0 10px 0 0;
          margin: 0;
          background-color: transparent !important;
          color: black !important;
        }
        .el-select__selected-item {
          color: black !important;
        }
        .el-select__suffix {
          display: none;
        }
      }
    }
  }
  .pdf-address {
    border-bottom: 1px solid #000;
    font-size: var(--el-font-size-base);
    padding-left: 1em;
  }
}
</style>
